/**
 * Die Eingabezeile zeigt, was sie meint — fett ist fett, nicht `*fett*`.
 *
 * **Warum das nicht in einem `<textarea>` geht.** Ein Textfeld kann eine
 * Schrift haben, nicht zwei. Wer darin fett darstellen wollte, müsste hinter
 * das Feld eine zweite, gestaltete Kopie legen und das Feld selbst
 * durchsichtig machen — und dann laufen die beiden auseinander, sobald ein
 * Wort fett breiter ist als dasselbe Wort mager. Der Cursor stünde neben dem
 * Buchstaben. Das ist die bekannte Falle dieses Tricks, und sie ist nicht zu
 * umgehen.
 *
 * Also ein `contenteditable`. Damit kommen zwei Pflichten, und beide stehen
 * hier:
 *
 * - **Gespeichert wird weiter Text mit Zeichen**, `Das war *großartig*`.
 *   Nicht HTML. Der Grund steht in `auszeichnung.ts` und gilt unverändert:
 *   derselbe Text steht in der Vorschau der Chatliste und auf dem
 *   Sperrbildschirm, und dort gibt es keine Formatierung. `lese()` macht aus
 *   dem Feld wieder Zeichen, `schreibe()` aus Zeichen wieder ein Feld.
 * - **Was hineinkommt, ist Text.** Eingefügtes aus fremden Seiten bringt
 *   fremdes HTML mit; `alsText()` nimmt beim Einfügen ausschließlich die
 *   Zeichen. Ein Chat, in dem jemand Markup unterbringen kann, ist keiner —
 *   und das gilt beim Tippen genauso wie beim Anzeigen.
 */

import { zerlege, type Art } from './auszeichnung'
import { findeLinks } from './links'

/** Welches Element welche Auszeichnung trägt. */
const VON_TAG: Record<string, Art> = {
  B: 'fett',
  STRONG: 'fett',
  I: 'kursiv',
  EM: 'kursiv',
  S: 'durch',
  STRIKE: 'durch',
  DEL: 'durch',
}

/** Und das Zeichen dazu. */
const ZEICHEN: Record<Exclude<Art, 'text'>, string> = {
  fett: '*',
  kursiv: '_',
  durch: '~',
  kapitael: '^',
}

/** Die Klasse für Kapitälchen — dafür gibt es kein eigenes Element. */
export const KAPITAEL = 'aus--kapitael'

/**
 * What the field says, as text with characters.
 *
 * **Not nested**, as `auszeichnung.ts` lays down. Where one mark lies inside
 * another, **the inner one holds for that part** (1.185.2):
 * `<i>a<span class="aus--kapitael">b</span>c</i>` reads `_a_^b^_c_`. Up to
 * 1.185.1 the outer one won and the inner one went silently: whoever put
 * small caps on an italic word saw them in the field and lost them on saving,
 * and with `ordne()` would not even have seen them. The inner one is the one
 * chosen last.
 */
export function lese(wurzel: HTMLElement): string {
  // Pieces of text with the mark that holds them; the innermost one counts.
  const stuecke: { art: Exclude<Art, 'text'> | null; text: string }[] = []
  let bisher = ''
  const dazu = (art: Exclude<Art, 'text'> | null, text: string): void => {
    stuecke.push({ art, text })
    bisher += text
  }

  const geheDurch = (knoten: Node, art: Exclude<Art, 'text'> | null): void => {
    for (const kind of Array.from(knoten.childNodes)) {
      if (kind.nodeType === Node.TEXT_NODE) {
        dazu(art, kind.textContent ?? '')
        continue
      }
      if (!(kind instanceof HTMLElement)) continue

      if (kind.tagName === 'BR') {
        dazu(art, '\n')
        continue
      }

      // Safari packt jede weitere Zeile in ein `div`. Ein Umbruch davor, aber
      // nur einer -- sonst entstünde nach jedem Absatz eine Leerzeile.
      if (kind.tagName === 'DIV' || kind.tagName === 'P') {
        if (bisher !== '' && !bisher.endsWith('\n')) dazu(art, '\n')
        geheDurch(kind, art)
        continue
      }

      geheDurch(kind, artVon(kind) ?? art)
    }
  }
  geheDurch(wurzel, null)

  // Neighbours with the same mark are one run: `<i>a</i><i>b</i>` is `_ab_`.
  const laeufe: { art: Exclude<Art, 'text'> | null; text: string }[] = []
  for (const stueck of stuecke) {
    const letzter = laeufe.at(-1)
    if (letzter !== undefined && letzter.art === stueck.art) letzter.text += stueck.text
    else laeufe.push({ ...stueck })
  }

  let raus = ''
  for (const { art, text } of laeufe) {
    if (art === null) {
      raus += text
      continue
    }
    // The mark goes round each line and not round the break (1.185.2).
    // `schreibe()` builds a mark per line, and `zerlege()` knows no mark
    // across a break; `_a<br>b_` stood in the text as it was typed, with the
    // underscores. The browser leaves exactly that behind: a deleted italic
    // word becomes `<i><br></i>`, and that read as `_\n_`.
    const zeichen = ZEICHEN[art]
    raus += text
      .split('\n')
      // Ein leeres Paar wäre nur ein Zeichenpaar im Text -- weg damit.
      .map((zeile) => (zeile === '' ? '' : zeichen + zeile + zeichen))
      .join('\n')
  }
  return raus
}

/**
 * Whether the field holds only what `schreibe()` would put there (1.185.2).
 *
 * From use: „wenn man ändern als Textbeitrags ein Smiley angibt verhält sich
 * das System komisch … Insbesondere auch wenn man eine Formulierung
 * verwendet, wie kursiv, Kapitälchen". Measured in Chromium: when marked text
 * is deleted, the browser keeps its look as the *typing style* and writes it
 * back as `<span style="font-variant-caps: all-small-caps; …">` around
 * whatever is typed next. Three things followed, and each looked like the
 * field had gone wrong:
 *
 * - everything typed afterwards was small caps, with no mark in sight;
 * - the button could not take it off: `kapitaelchen()` looks for the class
 *   and finds a style;
 * - a second mark inside it was nested, and `lese()` drops the inner one, so
 *   it was gone on saving.
 *
 * Allowed is: text, `<br>`, a line (`<div>`, `<p>`, as Safari makes them) and
 * one level of mark as `schreibe()` writes it -- `<b>`, `<i>`, `<s>`,
 * `span.aus--kapitael`, all without a `style`. Anything else (a style, a mark
 * in a mark, a `<font>`, a picture) means the field is written anew.
 */
function geordnet(wurzel: HTMLElement): boolean {
  const pruefe = (knoten: Node, inMarke: boolean): boolean => {
    for (const kind of Array.from(knoten.childNodes)) {
      if (kind.nodeType === Node.TEXT_NODE) continue
      if (!(kind instanceof HTMLElement)) return false
      if (kind.tagName === 'BR') {
        if (kind.attributes.length > 0) return false
        continue
      }
      if (kind.hasAttribute('style')) return false
      if (kind.tagName === 'DIV' || kind.tagName === 'P') {
        if (inMarke || kind.attributes.length > 0) return false
        if (!pruefe(kind, false)) return false
        continue
      }
      const eigene =
        (['B', 'I', 'S'].includes(kind.tagName) && kind.attributes.length === 0) ||
        (kind.tagName === 'SPAN' && kind.attributes.length === 1 && kind.className === KAPITAEL)
      if (!eigene || inMarke) return false
      if (!pruefe(kind, true)) return false
    }
    return true
  }
  return pruefe(wurzel, false)
}

/**
 * Where the selection stands, as characters before each of its ends --
 * counted as `lese()` counts them, without the marks.
 */
function auswahlAlsZeichen(wurzel: HTMLElement): [number, number] | null {
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0) return null
  const bereich = auswahl.getRangeAt(0)
  if (!wurzel.contains(bereich.startContainer) || !wurzel.contains(bereich.endContainer)) return null
  const bis = (knoten: Node, versatz: number): number => {
    const davor = document.createRange()
    davor.selectNodeContents(wurzel)
    davor.setEnd(knoten, versatz)
    const kasten = document.createElement('div')
    kasten.append(davor.cloneContents())
    return klartext(kasten).length
  }
  return [bis(bereich.startContainer, bereich.startOffset), bis(bereich.endContainer, bereich.endOffset)]
}

/** What `lese()` reads, without the characters of the marks. */
function klartext(wurzel: HTMLElement): string {
  let raus = ''
  const geheDurch = (knoten: Node): void => {
    for (const kind of Array.from(knoten.childNodes)) {
      if (kind.nodeType === Node.TEXT_NODE) raus += kind.textContent ?? ''
      else if (kind instanceof HTMLElement && kind.tagName === 'BR') raus += '\n'
      else if (kind instanceof HTMLElement && (kind.tagName === 'DIV' || kind.tagName === 'P')) {
        if (raus !== '' && !raus.endsWith('\n')) raus += '\n'
        geheDurch(kind)
      } else geheDurch(kind)
    }
  }
  geheDurch(wurzel)
  return raus
}

/** A count of characters back to a point, in a field `schreibe()` wrote. */
function stelleBei(wurzel: HTMLElement, zeichen: number): [Node, number] {
  let rest = zeichen
  let stelle: [Node, number] | null = null
  const laufe = (knoten: Node): void => {
    for (const kind of Array.from(knoten.childNodes)) {
      if (stelle !== null) return
      if (kind.nodeType === Node.TEXT_NODE) {
        const laenge = kind.textContent?.length ?? 0
        if (rest <= laenge) {
          stelle = [kind, rest]
          return
        }
        rest -= laenge
      } else if (kind instanceof HTMLElement && kind.tagName === 'BR') {
        if (rest === 0) {
          stelle = [knoten, Array.from(knoten.childNodes).indexOf(kind)]
          return
        }
        rest -= 1
      } else laufe(kind)
    }
  }
  laufe(wurzel)
  return stelle ?? [wurzel, wurzel.childNodes.length]
}

/** The selection back to its two counts of characters. */
function setzeAuswahl(wurzel: HTMLElement, [von, bis]: [number, number]): void {
  const bereich = document.createRange()
  bereich.setStart(...stelleBei(wurzel, von))
  bereich.setEnd(...stelleBei(wurzel, bis))
  const auswahl = window.getSelection()
  auswahl?.removeAllRanges()
  auswahl?.addRange(bereich)
}

/**
 * Put the field back into the form `schreibe()` gives it, if the browser has
 * left anything else behind -- with the caret or the selection where it was
 * (1.185.2).
 *
 * **Only when needed, never while typing normally**: a field that is in
 * order is left alone, because every rewrite costs the browser's own undo
 * and, on iOS, a pending correction. And never while a word is being
 * composed (the caller asks), because a composition lives in the text node
 * this would replace.
 *
 * Setting the caret anew also ends the typing style: the browser keeps it
 * only as long as the caret does not move, and this moves it.
 *
 */
export function ordne(wurzel: HTMLElement): void {
  if (geordnet(wurzel)) return
  const auswahl = auswahlAlsZeichen(wurzel)
  schreibe(wurzel, lese(wurzel))
  if (auswahl !== null) setzeAuswahl(wurzel, auswahl)
}

/** Die Auszeichnung eines Elements — oder `null`. */
function artVon(el: HTMLElement): Exclude<Art, 'text'> | null {
  const vomTag = VON_TAG[el.tagName]
  if (vomTag !== undefined && vomTag !== 'text') return vomTag
  if (el.classList.contains(KAPITAEL)) return 'kapitael'
  // Der Browser setzt bei `styleWithCSS` mitunter einen Stil statt eines
  // Elements. Beides muss gelesen werden, sonst geht die Auszeichnung beim
  // Senden verloren.
  if (el.style.fontWeight === 'bold' || Number(el.style.fontWeight) >= 600) return 'fett'
  if (el.style.fontStyle === 'italic') return 'kursiv'
  if (el.style.textDecorationLine.includes('line-through')) return 'durch'
  if (el.style.fontVariantCaps !== '') return 'kapitael'
  return null
}

/**
 * Text mit Zeichen ins Feld schreiben — als gestaltete Stücke.
 *
 * Gebraucht beim Leeren nach dem Senden und beim Wiederherstellen eines
 * Entwurfs. Beim Tippen wird **nie** geschrieben: das Feld ist dann die
 * Wahrheit, und wer es aus dem Zustand nachzieht, setzt den Cursor ans Ende.
 */
export function schreibe(wurzel: HTMLElement, text: string): void {
  wurzel.replaceChildren()
  // An address stays whole and plain (1.113.0, `findeLinks`): the `_` in
  // `…/a_b_c` is not italic here either.
  const stuecke = findeLinks(text).flatMap((teil) =>
    teil.art === 'link' ? [{ art: 'text' as const, text: teil.text }] : zerlege(teil.text),
  )
  for (const stueck of stuecke) {
    for (const [i, zeile] of stueck.text.split('\n').entries()) {
      if (i > 0) wurzel.append(document.createElement('br'))
      if (zeile === '') continue
      wurzel.append(stueck.art === 'text' ? zeile : gestaltet(stueck.art, zeile))
    }
  }
}

function gestaltet(art: Exclude<Art, 'text'>, text: string): HTMLElement {
  if (art === 'kapitael') {
    const span = document.createElement('span')
    span.className = KAPITAEL
    span.textContent = text
    return span
  }
  const el = document.createElement(art === 'fett' ? 'b' : art === 'kursiv' ? 'i' : 's')
  el.textContent = text
  return el
}

/**
 * Eingefügtes wird Text und sonst nichts.
 *
 * `insertText` und nicht `insertHTML`: was aus einer fremden Seite kommt,
 * bringt deren Markup mit — Verweise, Schriftgrößen, mitunter ein Skript.
 * Hier interessiert der Wortlaut.
 */
export function alsText(text: string): void {
  document.execCommand('insertText', false, text.replace(/\r\n?/g, '\n'))
}

/**
 * Eine Auszeichnung auf die Auswahl legen — oder wieder wegnehmen.
 *
 * **`execCommand` ist abgekündigt und trotzdem richtig hier.** Es ist das
 * einzige, was in jedem Browser die Auswahl korrekt trennt, den Cursor
 * behält und in den Rückgängig-Stapel des Browsers einträgt — das alles von
 * Hand mit `Range` nachzubauen wäre ein eigenes Projekt mit eigenen Fehlern.
 * Für Kapitälchen gibt es keinen Befehl; dort wird von Hand gehüllt.
 */
export function zeichneAus(art: Exclude<Art, 'text'>): void {
  if (art === 'kapitael') {
    kapitaelchen()
    return
  }
  document.execCommand(
    art === 'fett' ? 'bold' : art === 'kursiv' ? 'italic' : 'strikeThrough',
  )
}

/** Kapitälchen von Hand — hüllen, oder die Hülle wieder auflösen. */
function kapitaelchen(): void {
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0 || auswahl.isCollapsed) return
  const bereich = auswahl.getRangeAt(0)

  // Steckt die Auswahl schon in einer Hülle, wird sie aufgelöst -- derselbe
  // Knopf, zurück.
  const drin = (bereich.commonAncestorContainer instanceof HTMLElement
    ? bereich.commonAncestorContainer
    : bereich.commonAncestorContainer.parentElement
  )?.closest(`.${KAPITAEL}`)
  if (drin !== null && drin !== undefined) {
    const eltern = drin.parentNode
    if (eltern !== null) {
      while (drin.firstChild !== null) eltern.insertBefore(drin.firstChild, drin)
      eltern.removeChild(drin)
      eltern.normalize()
    }
    return
  }

  const huelle = document.createElement('span')
  huelle.className = KAPITAEL
  try {
    bereich.surroundContents(huelle)
  } catch {
    // Geht über Elementgrenzen hinweg nicht -- dann lieber nichts tun als
    // den Text zerlegen. Eine Auswahl innerhalb einer Zeile trifft es immer.
    return
  }
  auswahl.removeAllRanges()
  const neu = document.createRange()
  neu.selectNodeContents(huelle)
  auswahl.addRange(neu)
}

/** Ob die aktuelle Auswahl in diesem Feld liegt und etwas umfasst. */
export function auswahlIn(feld: HTMLElement | null): boolean {
  if (feld === null) return false
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0 || auswahl.isCollapsed) return false
  return feld.contains(auswahl.getRangeAt(0).commonAncestorContainer)
}
